import { Button } from "@features/atoms/Button"
import { useCheckout } from "@features/tickets/useCheckout"
import { formatDollars } from "@utils/format-dollars"
import { cn } from "@utils/tw"
import { defineBlock, InnerBlocks } from "eddev/blocks"
import { useState } from "react"
import { MembershipGridContext } from "../../contexts/MembershipGridContext"

export const meta: BlockMeta = {
  title: "Membership Grid",
  description: "Displays the available membership levels in an extended grid",
  category: "text",
  icon: "admin-users",
  tags: ["#page"],
  flags: {
    fullWidth: true,
  },
}

const Cell = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <div className={cn("shrink-0 p-4 border-r border-grey2 last:border-none flex justify-start items-end", className)}>
    {children}
  </div>
)

export default defineBlock("memberships/membership-grid", (props) => {
  const checkout = useCheckout()
  const levels = props.miscSettings?.memberships?.membershipLevels ?? []
  const [mode, setMode] = useState<"premium" | "standard">("standard")
  const [purchasingId, setPurchasingId] = useState<string | null>(null)

  return (
    <div className="content-sized overflow-auto block-spacing">
      <div className="flex flex-col justify-center items-center min-w-fit">
        <div className="flex justify-center shrink-0 shadow-header w-fit">
          {/* HEADERS */}
          <Cell className="w-membershipGridCol1W">
            <div className="flex items-center flex-nowrap">
              <Button
                onClick={() => setMode("standard")}
                className={cn(mode === "standard" ? "opacity-100" : "opacity-50")}
                variant="filled"
              >
                Standard
              </Button>
              <Button
                onClick={() => setMode("premium")}
                className={cn(mode === "premium" ? "opacity-100" : "opacity-50")}
                variant="filled"
              >
                Premium
              </Button>
            </div>
          </Cell>
          {levels.map((level, i) => {
            let price = formatDollars(level?.priceStandard!)
            if (mode === "premium" && level?.hasPremium) {
              price = formatDollars(level?.pricePremium!)
            }
            const membershipId =
              mode === "premium" && level?.hasPremium ? level?.membershipIdPremium : level?.membershipIdStandard

            return (
              <Cell key={i} className="w-membershipGridColW text-blue">
                <div className="flex flex-col w-full">
                  <p className="type-body-s">{level?.label}</p>
                  <p className="type-label-s opacity-60">{price}</p>
                  <Button
                    className="mt-5"
                    onClick={async () => {
                      if (!membershipId) return
                      setPurchasingId(membershipId)
                      try {
                        await checkout.openMembershipPurchase(membershipId)
                      } finally {
                        setPurchasingId(null)
                      }
                    }}
                    disabled={!membershipId || !checkout.basket || checkout.isPending}
                    loading={!!membershipId && purchasingId === membershipId}
                    variant="outline"
                  >
                    {membershipId ? "Join" : "Currently unavailable"}
                  </Button>
                </div>
              </Cell>
            )
          })}
        </div>

        {/* BODY */}
        <div className="flex flex-col justify-center shrink-0 mt-7 w-fit">
          <MembershipGridContext.Provider
            value={{
              levelModes: levels.map((level) => (mode === "premium" && level?.hasPremium ? "premium" : "standard")),
            }}
          >
            <InnerBlocks
              allowedBlocks={["memberships/membership-grid-group"]}
              orientation="vertical"
              defaultBlocks={[
                ["memberships/membership-grid-group", {}],
                ["memberships/membership-grid-group", {}],
                ["memberships/membership-grid-group", {}],
              ]}
            />
          </MembershipGridContext.Provider>
        </div>
      </div>
    </div>
  )
})
